/**
 * Enhanced Logger with Namespace Support
 * 
 * Usage Examples:
 * 
 * 1. Basic usage (unchanged):
 *    import { logger } from './utils/logger';
 *    logger.log('This uses global debug settings');
 * 
 * 2. Namespaced logging:
 *    const fileLogger = logger.namespace('docsCRDT');
 *    fileLogger.log('This respects docsCRDT debug flag');
 * 
 * 3. Feature-specific logging:
 *    const guestLogger = logger.namespace('GUEST_DEMO');
 *    guestLogger.log('This shows only when GUEST_DEMO is enabled');
 * 
 * 4. Runtime control:
 *    import { setDebugFlag } from './utils/logger';
 *    setDebugFlag('docsCRDT', false); // Turn off docs CRDT logs
 *    setDebugFlag('auth', true);      // Turn on auth logs
 * 
 * 5. Check current status:
 *    import { getDebugFlag } from './utils/logger';
 *    if (getDebugFlag('PERFORMANCE')) {
 *      // Only run expensive logging operations when needed
 *    }
 * 
 * 6. NEW: Auto-file logging (recommended):
 *    import { createFileLogger } from './utils/logger';
 *    const log = createFileLogger(); // Automatically uses filename as namespace
 *    log.info('This will show with file context');
 * 
 * 7. NEW: Feature tagging:
 *    const log = createFileLogger('FEATURE_NAME');
 *    log.info('This will show when FEATURE_NAME is enabled');
 * 
 * 8. NEW: File-level debug control (no need to edit logger.ts!):
 *    const log = createFileLogger('FEATURE_NAME', { enabled: false });
 *    // This file's logs are now disabled, regardless of centralized config
 *    // You can also set enabled: true to force enable for this file only
 * 
 * 10. NEW: Smart string formatting:
 *    import { formatJSON, formatLongString } from './utils/logger';
 *    
 *    // For objects with long strings (automatically formats multi-line strings nicely):
 *    logger.log(formatJSON({ prompt: veryLongPrompt }, 'My prompt'));
 *    
 *    // For standalone long strings:
 *    logger.log(formatLongString(veryLongPrompt, 'Agentic prompt'));
 * 
 * 11. NEW: Configurable formatting options:
 *    logger.log(formatJSON(obj, 'Data', {
 *      maxStringLines: 10,           // Show first/last 10 lines for long multi-line strings
 *      preserveMultilineFormatting: true,  // Show multi-line strings in readable format
 *      smartLineTruncation: true,    // Truncate at line boundaries when possible
 *      maxStringLength: 1000         // Character limit for truncation
 *    }));
 * 
 * To add new debug flags, edit the DEBUG_FLAGS object below.
 */

// Import centralized dev mode detection
import { IS_DEV } from './devMode';

const isDev = IS_DEV;

// Load saved debug flags from localStorage
const loadSavedDebugFlags = (): Record<string, boolean> => {
  try {
    const saved = localStorage.getItem('galOS_debug_flags');
    if (saved) {
      const parsed = JSON.parse(saved);
      return typeof parsed === 'object' && parsed !== null ? parsed : {};
    }
  } catch (error) {
    console.warn('[Logger] Failed to load saved debug flags:', error);
  }
  return {};
};

// Save debug flags to localStorage
const saveDebugFlags = (flags: Record<string, boolean>) => {
  try {
    localStorage.setItem('galOS_debug_flags', JSON.stringify(flags));
  } catch (error) {
    console.warn('[Logger] Failed to save debug flags:', error);
  }
};

// Default debug configuration - add file/namespace flags here
const DEFAULT_DEBUG_FLAGS = {
  '*': isDev, // Only enable in development, disable in production
  'ACTION_EVENT_SERVICE': false, // Enable action event service logging
  'ACTION_REGISTRY': false, // Enable action registry logging
  'ACTION_STACK': false, // Enable action stack logging
  'ACTIVITY_SERVICE': false, // Enable activity service logging
  'ADMIN_DASHBOARD': false,
  'ANALYTICS_SERVICE': false, // Enable analytics service logging
  'API_CALLS': false, // Enable API call logging
  'APP_INIT': false, // Enable app initialization logging
  'APP_REGISTRY': false, // Enable app registry logging
  'APP_REGISTRY_SERVICE': false, // Enable app registry service logging
  'auth': false, // Enable auth debugging
  'BANK_APP': false,
  'BANK_APP_COMMANDS': false, // Enable BankApp commands logging
  'BANK_APP_COMMANDS_SERVICE': false, // Enable BankApp commands service logging
  'BANK_APP_STORE': false, // Enable BankApp store logging
  'BANK_PLAID_ACCOUNTS': false,
  'BANK_PLAID_CONNECTOR': false,
  'BANK_PLAID_TRANSACTIONS': false,
  'BANK_TRANSACTION_TAGS': false,
  'BROWSER_APP': false,
  'BROWSER_APP_ACTIONS': false, // Enable BrowserApp actions logging
  'CACHE': false, // Enable cache operation logging
  'CALLBACK_UTILS': false, // Enable callback utils logging
  'CHAT_APP': true,
  'CHAT_APP_STREAM': true, // Enable chat app stream logging
  'CHAT_APP_THREAD_SERVICE': true, // Enable chat app thread service logging
  'CHAT_APP_PERSISTENCE': true, // Enable chat app persistence logging
  'CHAT_MODEL_SELECTOR': true,
  'CHAT_SERVICE': true, // Enable chat service logging
  'CRISP_CHAT_SERVICE': true, // Enable Crisp chat service logging
  'USE_SUPPORT_CHAT': true, // Enable support chat hook logging
  'CHAT_THREAD_SIDEBAR': true,
  'CLAUDE_CLIENT': false, // Enable Claude client logging
  'ClaudeGeneration': true,
  'COLLABORATION': false, // Disable collaboration logs
  'COMMAND_REGISTRY': false, // Enable command registry logging
  'COMMANDS': false, // Enable commands system logging
  'CONSOLE_APP': false,
  'CONTEXT_STORE': false, // Enable contextStore logging - DISABLED to reduce noise
  'CRDT_SERVICE_WS': false, // Enable CRDT websocket logging
  'CRDT_SYNC': false, // Enable CRDT synchronization logging
  'DEBUG_PANEL': false, // Enable debug panel logging
  'DEBUG_PAYMENT': false, // Enable debug payment logging
  'DEBUG_SERVICE': true,
  'DEBUG_UTIL': false, // Enable debug utility logging
  'DOCS_APP': true, // Enable DocsApp component logging
  'DOCS_APP_STORE': true, // Enable docsAppStore logging
  'DOCS_COMMANDS': true,
  'DOCS_COMMENT_PLUGIN': false,
  'DOCS_COMMENT_SERVICE': false,
  'DOCS_COMMENT_STORE': false,
  'DOCS_CONTENT_PARSER': false,
  'DOCS_HEADER': false,
  'DOCS_OVERVIEW': false,
  'DOCS_PROVIDER_REGISTRY': false,
  'DOCS_REVISION_INDICATOR': false,
  'DOCS_RIBBON': false,
  'DOCS_VERSIONS_VIEW': false,
  'DOCS_YJS_TEST': false,
  'DOCS2_STORE': false,
  'docsCRDT': false, // Enable full logging for docs CRDT
  'DOCUMENT_NAMING': false, // Enable document naming logging
  'DOCUMENT_SCREENSHOT_SERVICE': false, // Enable document screenshot service logging
  'SPACE_THUMBNAIL_SERVICE': true, // Enable space thumbnail capture/upload logging
  'SPACE_PAGE_WRAPPER': true, // Enable SpacePageWrapper initialization logging
  'DOM_PAGINATION': false, // Enable document pagination service logging
  'PAGINATION_DEBUG': false, // Enable dynamic pagination service logging
  'ENTITY_BROWSER_ROW': false,
  'HIERARCHY_STORE': true, // Enable hierarchy store logging
  'ENTITY_BROWSER2_APP': true,
  'ENTITY_BROWSER2_DROPZONE': true,
  'ENTITY_BROWSER2_FILTER': true,
  'ENTITY_BROWSER2_LIST': true,
  'ENTITY_OPERATIONS': false, // Enable entity command logging
  'ENTITY_SERVICE': false, // Enable entity service logging
  'ENTITY_STORE': true, // 🔥 ENABLE entity store logging (IndexedDB cache)
  'entityService': false, // Disable noisy entity service logs
  'ERROR_HANDLING': false, // Enable error handling logging
  'ESSAY_FLOW_MANAGER': false, // Enable essay flow manager logging
  'FILE_OPERATIONS': false, // Enable file operation logging
  'fileSystem': false, // Disable file system logs
  'FINDER_APP': false,
  'FINDER_FILE_PREVIEW': false,
  'FINDER_FILE_TREE': false,
  'FINDER_STORE': false,
  'FINDER_TOOLBAR': false,
  'FREEMIUM_STORE': false, // Enable freemium store logging
  'GA_CONFIG': false, // Enable Google Analytics config logging
  'GALIDE_APP': false,
  'GALIDE_EDITOR': false,
  'GALIDE_SERVICE': false, // Enable GalideApp service logging
  'GALIDE_TAB_BAR': false,
  'GALIDE_UI_RENDERER': false,
  'GALIDE_UI_VIEW_RENDERER': false,
  'GOOGLE_ANALYTICS_SERVICE': false, // Enable Google Analytics service logging
  'GUEST_DEMO': false, // Enable guest demo logs
  'IMAGE_VIEWER_APP': false,
  'INGEST_WIZARD_STORE': false,
  'LOGGING_DEVTOOLS': false,
  'MARKETING_BOX_PAGE': false,
  'MARKETING_SPACE_PAGE': false,
  'MARKETING_USE_CASE_LANDING': false,
  'MIXPANEL': false, // Enable mixpanel logging
  'MIXPANEL_CONFIG': false, // Enable mixpanel config logging
  'MODEL_SERVICE': false, // Enable modelService logging
  'MONGO_UTILS': false, // Enable mongo utils logging
  'NETWORK': false, // Enable network request logging
  'NEURVANA3_APP': false,
  'NEURVANA3_DUPLICATE_RESOLVER': false,
  'NEURVANA3_FILE_HIERARCHY': false, // Enable Neurvana3 file hierarchy logging
  'NEURVANA3_FILE_TABLE': false,
  'NEURVANA3_PREVIEW_MODAL': false,
  'NEURVANA3_SCANNER_PANEL': false,
  'NEURVANA3_SERVICE': false, // Enable Neurvana3 service logging
  'NOTES_APP': false,
  'NOTES_COMMANDS': false,
  'NOTES_CRDT_SERVICE': false,
  'NOTES_EDITOR': false,
  'NOTES_OVERVIEW': false,
  'NOTES_STORE': false,
  'NOTES_Y_EDITOR': false,
  'PDFIUM_COMMANDS': false,
  'PDFIUM_DEBUG': false,
  'PDFIUM_HIGHLIGHT_RENDERER': false, // Enable PDFium highlight renderer logging
  'PDFIUM_HOOKS': false, // Enable PDFium hooks logging
  'PDFIUM_PAGE': false,
  'PDFIUM_PAGE_VIEW': false,
  'PDFIUM_RENDERER': false,
  'PDFIUM_TEXT_LAYER': false,
  'PDFIUM_WASM': false, // Enable PDFium WASM logging
  'PDFIUM_WASM_VIEWER': false,
  'PDFIUM_WRAPPER': false,
  'PERFORMANCE': false, // Disable performance logs
  'PERSONAL_MODEL_APP': false,
  'PERSONAL_MODEL_DETAILS': false,
  'PERSONAL_MODEL_SERVICE': false, // Enable personal model service logging
  'PERSONAL_MODEL_TRAINING': false,
  'PRESENCE_SERVICE': false, // Enable presence service logging
  'ROOT_LANDING': false,
  'SCHEMA_VALIDATOR': false, // Enable schema validator logging
  'SELECTION_SERVICE': false,
  'SETTINGS_ACCOUNT_TAB': false,
  'SETTINGS_APP': false,
  'SETTINGS_HEALTH_SECTION': false,
  'SPACE_CHAT_MESSAGE_UTILS': false,
  'SPACE_COMMANDS': false, // Enable space commands logging
  'SPACE_INIT_TESTS': false, // Enable space initialization tests logging
  'SPACECHAT_DEBUG': true, // Enable SpaceChat debug logging
  'SPACE_SERVICE': false, // Enable space service logging
  'SPACE_STORE': true, // Enable space store logging
  'STATE_CHANGES': false, // Enable state change logging
  'SYSTEM': false, // Enable system logging
  'toolDiscovery': false, // Enable tool discovery debugging
  'USER_COMPATIBILITY': false, // Enable user compatibility logging
  'USER_INTERACTIONS': false, // Enable user interaction logging
  'USER_MESSAGE_SERVICE': false, // Enable user message service logging
  'USER_STORE': false, // Enable userStore logging
  'WEB_DOCUMENT_APP': false,
  'WIZARD_APP': true,
  'MicroWizardRunner': true,
  'ResponseProcessor': true,
  'workflowOrchestrator': false, // Enable workflow orchestrator debugging
  'WORKFLOWS_APP': false,
  'WORKFLOWS_APP_COMMANDS': false, // Enable workflows app commands logging
  'WORKFLOWS_APP_STORE': false, // Enable workflows app store logging
  'yDocHighLevelAPI': false, // Enable YDoc high-level API debugging
  'yDocLowLevelAPI': false, // Enable YDoc low-level API debugging
  'YJS_MARKDOWN_BRIDGE': false, // Enable YJS markdown bridge logging
  'LOGINGUI': false,
  'ENTITIES_APP': false,
  'CHATAPPSTORE_APP': false,
  'CONTENT_APP': false,
  'EDIT_APP': false,
  'NODE_APP': false,
  'SELECTOR_APP': false,
  'TEXT_APP': false,
  'CHANGEDECORATIONCONTEXT_APP': false,
  'DOCSCRDTSERVICE_APP': true,
  'DOCSVERSIONSERVICE_APP': false,
  'AUTOIDPLUGIN_APP': false,
  'CHANGEDECORATIONPLUGINREACT_APP': false,
  'CHANGEDIFFPLUGIN_APP': false,
  'RESOLVER_APP': false,
  'REGISTRY_APP': false,
  'COMMANDS_APP': false,
  'ENTITYBROWSERAPP2STORE_APP': false,
  'ENTITYHIERARCHY_APP': false,
  'GALIDEEXPLORER_APP': false,
  'NEURVANAEXPLORER_APP': false,
  'NEURVANASERVICE_SERVICE': false,
  'NOTESAPPSTORE_APP': false,
  'PDFIUMAPP_APP': false,
  'PDFPAGEWITHTEXTLAYER_APP': false,
  'PERFORMANCEMONITOR_APP': false,
  'PDFIUMSTORE_APP': false,
  'ADMINPROTECTEDROUTE_COMPONENT': false,
  'GALBOXSTORE_COMPONENT': false,
  'GUESTMARKETINGLAYOUT_COMPONENT': false,
  'ONBOARDINGCHECKLIST_COMPONENT': false,
  'PROTECTEDROUTE_COMPONENT': false,
  'COMMANDMODETOGGLE_COMPONENT': false,
  'MODELSELECTOR_COMPONENT': false,
  'SIGNUP_MODAL': true,
  'USESPACECHATINTERFACE_COMPONENT': true,
  'COMMANDEXECUTOR_SERVICE': true,
  'COMMANDFILTERSERVICE_SERVICE': false,
  'TOOLUSEORCHESTRATOR_SERVICE': true,
  'UISTORE_COMPONENT': false,
  'SIMPLEWINDOWTARGETING_COMPONENT': false,
  'TOOLDISCOVERY_COMPONENT': true,
  'WINDOW_COMPONENT': false,
  'ENTITYHANDLER': false,
  'USEGALBOXSERVER': false,
  'CONTEXTSERVICE_SERVICE': false,
  'INTEGRATIONSYSTEM_SERVICE': false,
  'USECASESYSTEM_SERVICE': false,
  'CRDTSERVICE_SERVICE': false,
  'CRDTSERVICEWS_SERVICE': false,
  'ENTITYBROWSERAPPCRDTSERVICE_SERVICE': false,
  'INTEGRATIONSERVICE_SERVICE': false,
  'MODELSERVICE_SERVICE': false,
  'NOTESSERVICE_SERVICE': false,
  'SPACEMANAGEMENTSERVICE_SERVICE': false,
  'ENTITYBROWSERAPPSTORE_STORE': false,
  'SPACEINITIALIZATIONDEBUGGER': false,
  'DOCS_APPLYREDFLAGEDITS': false,
  'DOCS_APPLYREDFLAGEDITSSTANDARD': false,
  'RED-FLAG-COMMENTS-WORKFLOW': false,
  'RED-FLAG-WORKFLOW': false,
  'REDFLAGCOMMANDS': false,
  'WORKFLOW_RENDERMARKDOWN': false,
  'WORKFLOW_SENDANALYSIS': false,
  'MARKETINGCOMMANDS': false,
  'COMMENTCOMMANDS_APP': false,
  'DOCSEDITOR_APP': true,
  'DOCSYPROSEMIRROREDITOR_APP': true,
  'BUBBLEMENUPLUGIN_APP': false,
  'SAFEPLUGIN_APP': false,
  'YJSEXECUTOR_APP': false,
  'COMMENTSERVICE_SERVICE': false,
  'PRECISETRANSFORM_SERVICE': false,
  'TEXTMUTATIONS_SERVICE': false,
  'YDOCHIGHLEVELAPI_SERVICE': false,
  'YDOCLOWLEVELAPI_SERVICE': false,
  'LINEARPROJECTION_SERVICE': false,
  'RANGERESOLVER_SERVICE': false,
  'SNAPSHOTSERVICE_SERVICE': false,
  'STABLENODEREGISTRY_SERVICE': false,
  'YJSUNIFIED_SERVICE': false,
  'YJSCOMMANDS_APP': false,
  'AISUGGESTIONUTILS_APP': false,
  'EDITORUTILS_APP': false,
  'FLOATING_APP': false,
  'SELECTIONUTILS_APP': false,
  'WORKFLOWORCHESTRATOR_SERVICE': true,
  'MARKDOWN_APP': true,
  'AICOMMANDS': true,
  'AGENTIC_FLOW': true, // Enable agentic flow logging
  'GOOGLE_DRIVE_APP': true,
  'GOOGLE_SHEET_READ': true,
  'GOOGLE_DOC_READ': true,
  'GOOGLE_DOC_WRITE': true,
  'GOOGLE_SHEET_WRITE': true,
  'TOOL_SELECTOR': true,
  'SESSION_CONTEXT_BUILDER': true,
};

// Initialize DEBUG_FLAGS with saved values or defaults
const DEBUG_FLAGS = { ...DEFAULT_DEBUG_FLAGS, ...loadSavedDebugFlags() };

interface Logger {
  log: (...args: any[]) => void;
  error: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  info: (...args: any[]) => void;
  debug: (...args: any[]) => void;
  trace: (...args: any[]) => void;
  group: (...args: any[]) => void;
  groupEnd: () => void;
  groupCollapsed: (...args: any[]) => void;
  time: (label?: string) => void;
  timeEnd: (label?: string) => void;
}

interface NamespacedLogger extends Logger {
  namespace: (ns: string) => Logger;
}

// Check if a namespace should log
// Priority: file-level default > DEBUG_FLAGS[exact match] > DEBUG_FLAGS[partial match] > global flag
function shouldLog(namespace?: string, defaultEnabled?: boolean): boolean {
  if (!namespace) return defaultEnabled !== undefined ? defaultEnabled : DEBUG_FLAGS['*'];
  
  // File-level default takes highest priority (explicit override)
  if (defaultEnabled !== undefined) {
    return defaultEnabled;
  }
  
  // Check specific namespace in DEBUG_FLAGS
  if (namespace in DEBUG_FLAGS) {
    return DEBUG_FLAGS[namespace as keyof typeof DEBUG_FLAGS];
  }
  
  // Check for partial matches (e.g., 'docsCRDT' matches 'docsCRDT-Init')
  for (const [key, value] of Object.entries(DEBUG_FLAGS)) {
    if (key !== '*' && (namespace.includes(key) || key.includes(namespace))) {
      return value;
    }
  }
  
  // Fall back to global flag (lowest priority)
  return DEBUG_FLAGS['*'];
}

// Create a namespaced logger
function createNamespacedLogger(namespace: string, defaultEnabled?: boolean): Logger {
  const enabled = shouldLog(namespace, defaultEnabled);
  
  return {
    log: enabled ? console.log.bind(console) : () => {},
    error: enabled ? console.error.bind(console) : () => {},
    warn: enabled ? console.warn.bind(console) : () => {},
    info: enabled ? console.info.bind(console) : () => {},
    debug: enabled ? console.debug.bind(console) : () => {},
    trace: enabled ? console.trace.bind(console) : () => {},
    group: enabled ? console.group.bind(console) : () => {},
    groupEnd: enabled ? console.groupEnd.bind(console) : () => {},
    groupCollapsed: enabled ? console.groupCollapsed.bind(console) : () => {},
    time: enabled ? console.time.bind(console) : () => {},
    timeEnd: enabled ? console.timeEnd.bind(console) : () => {},

  };
}

// NEW: Auto-detect filename for automatic namespacing
function getCallerFileName(): string {
  try {
    const stack = new Error().stack;
    if (!stack) return 'unknown';
    
    const lines = stack.split('\n');
    // Skip the first few lines (Error constructor, this function, etc.)
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      // Look for a line that contains a file path
      const match = line.match(/\(?(.+?):(\d+):(\d+)\)?$/);
      if (match) {
        const filePath = match[1];
        // Extract just the filename without path and extension
        const fileName = filePath.split('/').pop()?.split('\\').pop()?.replace(/\.(ts|tsx|js|jsx)$/, '') || 'unknown';
        return fileName;
      }
    }
  } catch {
    // Fallback if stack trace parsing fails
  }
  return 'unknown';
}

// NEW: Create a logger that automatically uses the calling file's name as namespace
export function createFileLogger(featureTag?: string, options?: { enabled?: boolean }): Logger {
  const fileName = getCallerFileName();
  const namespace = featureTag ? `${fileName}:${featureTag}` : fileName;
  return createNamespacedLogger(namespace, options?.enabled);
}

// NEW: Create a logger with custom namespace and feature tag
export function createLogger(namespace: string, featureTag?: string, options?: { enabled?: boolean }): Logger {
  const fullNamespace = featureTag ? `${namespace}:${featureTag}` : namespace;
  return createNamespacedLogger(fullNamespace, options?.enabled);
}

// Default logger (maintains backward compatibility)
export const logger: NamespacedLogger = {
  log: shouldLog('*') ? console.log.bind(console) : () => {},
  error: shouldLog('*') ? console.error.bind(console) : () => {},
  warn: shouldLog('*') ? console.warn.bind(console) : () => {},
  info: shouldLog('*') ? console.info.bind(console) : () => {},
  debug: shouldLog('*') ? console.debug.bind(console) : () => {},
  trace: shouldLog('*') ? console.trace.bind(console) : () => {},
  group: shouldLog('*') ? console.group.bind(console) : () => {},
  groupEnd: shouldLog('*') ? console.groupEnd.bind(console) : () => {},
  groupCollapsed: shouldLog('*') ? console.groupCollapsed.bind(console) : () => {},
  time: shouldLog('*') ? console.time.bind(console) : () => {},
  timeEnd: shouldLog('*') ? console.timeEnd.bind(console) : () => {},

  
  // New namespace method
  namespace: (ns: string) => createNamespacedLogger(ns),
};

// Alternative: export individual methods for more granular control
// Note: devMode.ts now provides these utilities too, but keeping for backward compatibility
export const devLog = isDev ? console.log.bind(console) : () => {};
export const devError = isDev ? console.error.bind(console) : () => {};
export const devWarn = isDev ? console.warn.bind(console) : () => {};

// Store original console methods before any overrides
const ORIGINAL_CONSOLE = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  debug: console.debug.bind(console),
  trace: console.trace.bind(console),
  error: console.error.bind(console),
  warn: console.warn.bind(console),
  group: console.group.bind(console),
  groupEnd: console.groupEnd.bind(console),
  groupCollapsed: console.groupCollapsed.bind(console),
  time: console.time.bind(console),
  timeEnd: console.timeEnd.bind(console),
};

// Track if console is currently enabled for admin
let isConsoleEnabledForAdmin = false;

// Function to enable console for admin users
export function enableConsoleForAdmin(): void {
  if (isConsoleEnabledForAdmin) return;
  
  isConsoleEnabledForAdmin = true;
  
  // Restore all console methods
  console.log = ORIGINAL_CONSOLE.log;
  console.info = ORIGINAL_CONSOLE.info;
  console.debug = ORIGINAL_CONSOLE.debug;
  console.trace = ORIGINAL_CONSOLE.trace;
  console.error = ORIGINAL_CONSOLE.error;
  console.warn = ORIGINAL_CONSOLE.warn;
  console.group = ORIGINAL_CONSOLE.group;
  console.groupEnd = ORIGINAL_CONSOLE.groupEnd;
  console.groupCollapsed = ORIGINAL_CONSOLE.groupCollapsed;
  console.time = ORIGINAL_CONSOLE.time;
  console.timeEnd = ORIGINAL_CONSOLE.timeEnd;
  
  ORIGINAL_CONSOLE.log('[Logger] Console enabled for admin user');
}

// Function to disable console (back to production mode)
export function disableConsoleForAdmin(): void {
  if (!isConsoleEnabledForAdmin) return;
  
  isConsoleEnabledForAdmin = false;
  
  // Re-apply production overrides
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.trace = () => {};
  console.group = () => {};
  console.groupEnd = () => {};
  console.groupCollapsed = () => {};
  console.time = () => {};
  console.timeEnd = () => {};
  // TEMPORARILY ENABLED FOR DEBUGGING - keep errors/warnings enabled
  console.error = ORIGINAL_CONSOLE.error;
  console.warn = ORIGINAL_CONSOLE.warn;
  
  ORIGINAL_CONSOLE.log('[Logger] Console disabled (production mode)');
}

// Check if user is admin by verifying with backend (only in production)
export async function checkAndEnableAdminConsole(): Promise<void> {
  // In dev mode, console is already enabled for everyone - skip the check
  if (isDev) {
    return;
  }
  
  try {
    const authToken = localStorage.getItem('authToken');
    if (!authToken) {
      return;
    }
    
    // Dynamically import to avoid circular dependencies and early initialization issues
    const { API_BASE_URL } = await import('../api/config');
    const fetchService = (await import('../services/fetchService')).default;
    
    // Verify admin status from backend (can't be spoofed)
    const data = await fetchService(`${API_BASE_URL}/admin/check-access`);
    
    if (data.is_admin === true) {
      ORIGINAL_CONSOLE.log('[Logger] Admin user verified by backend, enabling console logs');
      enableConsoleForAdmin();
    }
  } catch {
    // Silently fail - don't enable console if we can't verify admin status
    // Don't log errors here to avoid noise for non-admin users
  }
}

// Global console override for production
if (!isDev) {
  // Override console methods in production to reduce noise
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.trace = () => {};
  console.group = () => {};
  console.groupEnd = () => {};
  console.groupCollapsed = () => {};
  console.time = () => {};
  console.timeEnd = () => {};
  
  // Keep errors and warnings - TEMPORARILY ENABLED FOR DEBUGGING
  // console.error = (...args) => ORIGINAL_CONSOLE.error('[PROD-ERROR]', ...args);
  // console.warn = (...args) => ORIGINAL_CONSOLE.warn('[PROD-WARN]', ...args);
  // Full console errors/warnings enabled for debugging
  console.error = ORIGINAL_CONSOLE.error;
  console.warn = ORIGINAL_CONSOLE.warn;
}

// Smart JSON trimming options
export interface LogJSONOptions {
  maxStringLength?: number;    // Truncate strings longer than this
  maxStringLines?: number;     // For multi-line strings, show first/last N lines
  maxArrayItems?: number;      // Show only first/last N items in arrays  
  maxObjectDepth?: number;     // Maximum nesting depth
  maxObjectProps?: number;     // Maximum properties per object
  showTruncationStats?: boolean; // Show what was truncated
  preserveMultilineFormatting?: boolean; // Show multi-line strings in readable format instead of JSON-escaped
  smartLineTruncation?: boolean; // Truncate at line boundaries when possible
  customFieldOptions?: {
    [key: string]: Partial<LogJSONOptions>;
  };
}

// Default options for smart trimming
const DEFAULT_LOG_OPTIONS: LogJSONOptions = {
  maxStringLength: 500,  // Much more aggressive - truncate any string > 500 chars
  maxStringLines: 5,     // Show first/last 5 lines for multi-line strings
  maxArrayItems: 3,      // Show first 3 and last 3 items max
  maxObjectDepth: 10,
  maxObjectProps: 50,
  showTruncationStats: true,
  preserveMultilineFormatting: true, // Enable better multi-line string display
  smartLineTruncation: true  // Prefer line-boundary truncation
};

// Helper function to format strings intelligently for logging
function formatStringForLogging(str: string, options: LogJSONOptions, stats: any): any {
  const maxLen = options.maxStringLength || 500;
  const maxLines = options.maxStringLines || 5;
  const preserveFormatting = options.preserveMultilineFormatting !== false;
  const smartTruncation = options.smartLineTruncation !== false;
  
  // Check if it's a multi-line string
  const lines = str.split('\n');
  const isMultiline = lines.length > 1;
  
  // If preserveFormatting is enabled and it's multi-line, format it even if not too long
  if (isMultiline && preserveFormatting) {
    
    if (lines.length > maxLines * 2) {
      // Show first/last lines
      stats.truncatedStrings++;
      const firstLines = lines.slice(0, maxLines);
      const lastLines = lines.slice(-maxLines);
      const omittedLines = lines.length - (maxLines * 2);
      
      const result = {
        __multilineString: true,
        preview: [
          ...firstLines,
          `... [${omittedLines} lines omitted (${str.length} total chars)] ...`,
          ...lastLines
        ].join('\n'),
        totalLines: lines.length,
        totalChars: str.length,
        truncated: true
      };
      return result;
    } else {
      // Multi-line but not too many lines, format it nicely
      return str;
    }
  }
  
  // If string is short enough and not multi-line (or formatting disabled), return as-is
  if (str.length <= maxLen) {
    return str;
  }
  
  // For single-line strings or when formatting is disabled
  stats.truncatedStrings++;
  
  if (smartTruncation && isMultiline) {
    // Try to truncate at line boundaries
    let truncated = '';
    let currentLength = 0;
    
    for (const line of lines) {
      if (currentLength + line.length + 1 > maxLen) {
        break;
      }
      if (truncated) truncated += '\n';
      truncated += line;
      currentLength += line.length + 1; // +1 for newline
    }
    
    return truncated + `... [+${str.length - truncated.length} chars, ${lines.length - truncated.split('\n').length} more lines]`;
  }
  
  // Default character-based truncation
  return str.substring(0, maxLen) + `... [+${str.length - maxLen} chars]`;
}

// Helper function to recursively trim object before JSON.stringify
function trimObject(obj: any, options: LogJSONOptions, stats: any, depth: number = 0): any {
  // Prevent infinite recursion
  if (depth > (options.maxObjectDepth || 10)) {
    stats.depthLimited++;
    return '[Object: Max depth reached]';
  }
  
  // Handle null/undefined
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  // Handle strings - smart truncation and formatting
  if (typeof obj === 'string') {
    return formatStringForLogging(obj, options, stats);
  }
  
  // Handle arrays - show first/last items if too long
  if (Array.isArray(obj)) {
    const maxItems = options.maxArrayItems || 3;
    if (obj.length > maxItems * 2) {
      stats.truncatedArrays++;
      const start = obj.slice(0, maxItems).map(item => 
        trimObject(item, options, stats, depth + 1)
      );
      const end = obj.slice(-maxItems).map(item => 
        trimObject(item, options, stats, depth + 1)
      );
      return [
        ...start,
        `... [${obj.length - (maxItems * 2)} more items] ...`,
        ...end
      ];
    }
    return obj.map(item => trimObject(item, options, stats, depth + 1));
  }
  
  // Handle objects - limit properties if too many
  if (typeof obj === 'object') {
    const entries = Object.entries(obj);
    const maxProps = options.maxObjectProps || 50;
    
    if (entries.length > maxProps) {
      stats.truncatedObjects++;
      const kept = entries.slice(0, maxProps);
      const result: any = {};
      
      kept.forEach(([key, value]) => {
        // Check for custom field overrides
        const fieldOptions = (options as any).customFieldOptions?.[key] || {};
        const mergedOptions = { ...options, ...fieldOptions };
        result[key] = trimObject(value, mergedOptions, stats, depth + 1);
      });
      
      result['...'] = `[${entries.length - maxProps} more properties]`;
      return result;
    }
    
    // Process all properties for smaller objects
    const result: any = {};
    entries.forEach(([key, value]) => {
      // Check for custom field overrides
      const fieldOptions = (options as any).customFieldOptions?.[key] || {};
      const mergedOptions = { ...options, ...fieldOptions };
      result[key] = trimObject(value, mergedOptions, stats, depth + 1);
    });
    return result;
  }
  
  return obj;
}

// Smart trimmer that processes the entire object before JSON.stringify
function createSmartReplacer(options: LogJSONOptions) {
  const stats = { 
    truncatedStrings: 0, 
    truncatedArrays: 0, 
    truncatedObjects: 0,
    depthLimited: 0
  };
  
  return { 
    trimObject: (obj: any) => trimObject(obj, options, stats, 0),
    stats 
  };
}

// Helper function to post-process JSON and format multi-line strings nicely
function formatMultilineStringsInJSON(jsonString: string): string {

  // Regular expression to find objects with __multilineString: true
  const multilineRegex = /\{\s*"__multilineString":\s*true,\s*"preview":\s*"([^"]*(?:\\.[^"]*)*)",\s*"totalLines":\s*(\d+),\s*"totalChars":\s*(\d+)(?:,\s*"truncated":\s*(true|false))?\s*\}/g;
  
  return jsonString.replace(multilineRegex, (match, preview, totalLines, totalChars) => {
    
    // Unescape the JSON string and format it nicely
    const unescapedPreview = preview
      .replace(/\\n/g, '\n')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
    
    const info = `(${totalLines} lines, ${totalChars} chars - truncated)`;
    
    // Format as a readable block instead of JSON
    return `"📄 Multi-line string ${info}:\n────────────────────────────────────────\n${unescapedPreview}\n────────────────────────────────────────"`;
  });
}

// Helper function that formats JSON with smart trimming and returns a string (preserves stack trace!)
export function formatJSON(
  obj: object, 
  label: string = '', 
  options: Partial<LogJSONOptions> = {},
  maxStringLength?: number
): string {
  const mergedOptions = { 
    ...DEFAULT_LOG_OPTIONS, 
    ...options,
    // Allow maxStringLength parameter to override options.maxStringLength
    ...(maxStringLength !== undefined && { maxStringLength })
  };
  const { trimObject: smartTrimObject, stats } = createSmartReplacer(mergedOptions);
  
  try {
    const trimmedObj = smartTrimObject(obj);
    const json = JSON.stringify(trimmedObj, null, 2);
    
    let output = json;
    
    // Post-process to format multi-line strings nicely
    if (mergedOptions.preserveMultilineFormatting) {
      output = formatMultilineStringsInJSON(output);
    }
    
    // Add truncation stats if enabled and something was truncated
    if (mergedOptions.showTruncationStats) {
      const hasTruncation = stats.truncatedStrings + stats.truncatedArrays + stats.truncatedObjects + stats.depthLimited > 0;
      if (hasTruncation) {
        const statsStr = [
          stats.truncatedStrings && `${stats.truncatedStrings} strings truncated`,
          stats.truncatedArrays && `${stats.truncatedArrays} arrays trimmed`, 
          stats.truncatedObjects && `${stats.truncatedObjects} objects trimmed`,
          stats.depthLimited && `${stats.depthLimited} depth-limited`
        ].filter(Boolean).join(', ');
        
        output += `\n📊 Truncation: ${statsStr}`;
      }
    }
    
    return label ? `${label}:\n${output}` : output;
  } catch (error) {
    // Fallback for circular references or other JSON errors
    return label ? `${label}: [Object: JSON stringify failed - ${error}]` : `[Object: JSON stringify failed - ${error}]`;
  }
}

// Export the debug flags for runtime modification
export const debugFlags = DEBUG_FLAGS;

// Utility to enable/disable namespaces at runtime
export function setDebugFlag(namespace: string, enabled: boolean): void {
  (DEBUG_FLAGS as any)[namespace] = enabled;
  saveDebugFlags(DEBUG_FLAGS);
}

// Utility to check current debug status
export function getDebugFlag(namespace: string): boolean {
  return shouldLog(namespace);
}

// NEW: Bulk operations for easier management
export function enableAllLogging(): void {
  Object.keys(DEBUG_FLAGS).forEach(key => {
    if (key !== '*') {
      (DEBUG_FLAGS as any)[key] = true;
    }
  });
  saveDebugFlags(DEBUG_FLAGS);
}

export function disableAllLogging(): void {
  Object.keys(DEBUG_FLAGS).forEach(key => {
    if (key !== '*') {
      (DEBUG_FLAGS as any)[key] = false;
    }
  });
  saveDebugFlags(DEBUG_FLAGS);
}

// NEW: Enable/disable by pattern
export function enableLoggingByPattern(pattern: string): void {
  Object.keys(DEBUG_FLAGS).forEach(key => {
    if (key !== '*' && key.toLowerCase().includes(pattern.toLowerCase())) {
      (DEBUG_FLAGS as any)[key] = true;
    }
  });
  saveDebugFlags(DEBUG_FLAGS);
}

export function disableLoggingByPattern(pattern: string): void {
  Object.keys(DEBUG_FLAGS).forEach(key => {
    if (key !== '*' && key.toLowerCase().includes(pattern.toLowerCase())) {
      (DEBUG_FLAGS as any)[key] = false;
    }
  });
  saveDebugFlags(DEBUG_FLAGS);
}

// NEW: Get all active debug flags
export function getActiveDebugFlags(): string[] {
  return Object.entries(DEBUG_FLAGS)
    .filter(([key, value]) => key !== '*' && value)
    .map(([key]) => key);
}

// NEW: Get all inactive debug flags
export function getInactiveDebugFlags(): string[] {
  return Object.entries(DEBUG_FLAGS)
    .filter(([key, value]) => key !== '*' && !value)
    .map(([key]) => key);
}

// NEW: Reset to default state
export function resetDebugFlags(): void {
  // Reset to original state (you might want to store original values)
  Object.keys(DEBUG_FLAGS).forEach(key => {
    if (key === '*') {
      (DEBUG_FLAGS as any)[key] = isDev;
    } else {
      (DEBUG_FLAGS as any)[key] = false;
    }
  });
  saveDebugFlags(DEBUG_FLAGS);
}

// NEW: Direct multi-line string formatter for quick logging
export function formatLongString(
  str: string, 
  label: string = '', 
  maxLines: number = 100,
  maxLength: number = 5000
): string {
  const lines = str.split('\n');
  
  if (str.length <= maxLength && lines.length <= maxLines) {
    return label ? `${label}:\n${str}` : str;
  }
  
  let output = '';
  const info = `(${lines.length} lines, ${str.length} chars)`;
  
  if (lines.length > maxLines) {
    const firstLines = lines.slice(0, Math.floor(maxLines / 2));
    const lastLines = lines.slice(-Math.floor(maxLines / 2));
    const omittedLines = lines.length - maxLines;
    
    output = [
      ...firstLines,
      `... [${omittedLines} lines omitted] ...`,
      ...lastLines
    ].join('\n');
  } else {
    output = str.length > maxLength 
      ? str.substring(0, maxLength) + `... [+${str.length - maxLength} chars]`
      : str;
  }
  
  const header = label ? `${label} ${info}:` : `Multi-line string ${info}:`;
  return `${header}\n────────────────────────────────────────\n${output}\n────────────────────────────────────────`;
} 